import { useStripe } from '@stripe/react-stripe-js';
import { Modal, PrimaryButton, SecondaryButton, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { updateUserPlan } from 'modules/profile/user-plan/api/user-plan-api';
import { BillingPeriodEnum } from 'modules/profile/user-plan/enums/billing-period-enum';
import { useUserPlan } from 'modules/profile/user-plan/hooks/use-user-plan';
import { UpgradeUserPlanInterface, UseUserPlanInterface, UserPlanInterface } from 'modules/profile/user-plan/types/user-plan-interface';
import { getStripeCustomerConfirmIntentMethod } from 'modules/profile/user-plan/utils/user-plan-stripe-utils';
import React, { useState } from 'react';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
export interface UpgradePlanConfirmationModalProps {
  pricePlan: UserPlanInterface;
  billingPeriod: BillingPeriodEnum;
  calculatedLink: string;
  cost: string;
  active?: boolean;
  paymentMethod?: UseUserPlanInterface['customer']['paymentMethod'];
  isPlanUpgradeAvailable?: boolean;
}
const UpgradePlanConfirmationModal = ({
  pricePlan,
  billingPeriod,
  calculatedLink,
  cost,
  paymentMethod,
  isPlanUpgradeAvailable
}: UpgradePlanConfirmationModalProps) => {
  const {
    t
  } = useLocoTranslation();
  const [opened, setOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');
  const {
    mutate
  } = useUserPlan();
  const stripeObject = useStripe();
  const onClose = () => {
    setOpened(false);
    setError('');
  };
  const onConfirm = async () => {
    const data: UpgradeUserPlanInterface = {
      userPlan: pricePlan.id,
      period: billingPeriod
    };
    try {
      const secureHandler = getStripeCustomerConfirmIntentMethod(stripeObject, paymentMethod);
      setIsFetching(true);
      const response = await updateUserPlan(data);
      if (response.data.redirectUrl) {
        window.location.assign(response.data.redirectUrl);
        return;
      }
      if (response.data.intentClientSecret && secureHandler) {
        const result = await secureHandler(response.data.intentClientSecret);
        if (result.error) {
          setError('Payment intent confirmation failed: ' + result.error.message);
          return;
        }
      }
      await mutate(data_0 => {
        if (data_0) {
          return {
            ...data_0,
            userPlan: {
              ...pricePlan
            }
          };
        }
      }, false);
      toast.success(t('dashboard.settings.user_plan.upgrade_success_title'));
      onClose();
    } catch (e) {
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''));
        }
      } else {
        setError(t('core.error.internal_error_message'));
      }
    } finally {
      setIsFetching(false);
    }
  };
  const onModalClose = () => setError('');
  return <>
      <PrimaryButton onClick={() => setOpened(true)} disabled={!isPlanUpgradeAvailable} className="bg-brand-primary hover:bg-background-secondary hover:text-brand-primary hover:border-brand-primary disabled:bg-text-secondary flex border-[1px] px-0 hover:border-solid lg:text-base" testAttributePostfix={`button-plan-${pricePlan.title.toLowerCase()}-upgrade`}>
        <strong className="truncate">{t('dashboard.settings.user_plan.upgrade_plan_title')}</strong>
      </PrimaryButton>
      <Modal opened={opened} onClose={onClose} title={t('dashboard.settings.user_plan.upgrade_confirmation_title')} afterLeave={onModalClose} isFetching={isFetching} isCentered={true}>
        <form className="flex flex-col gap-6">
          <div className="flex flex-col items-center justify-center gap-1">
            <div>
              {t('dashboard.settings.user_plan.upgrade_price_title')}: {cost}
            </div>
            <div className="flex">
              {t('dashboard.settings.user_plan.upgrade_confirmation_text')}
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <SecondaryButton width="large" onClick={(e_0: React.MouseEvent<HTMLButtonElement>) => {
            e_0.preventDefault();
            onClose();
          }} disabled={isFetching}>
              {t('confirmation_modal.cancel')}
            </SecondaryButton>
            <PrimaryButton testAttributePostfix={'confirm-plan-upgrade'} isFetching={isFetching} width="large" onClick={async (e_1: React.MouseEvent<HTMLButtonElement>) => {
            e_1.preventDefault();
            await onConfirm();
          }} type="submit">
              {t('confirmation_modal.confirm')}
            </PrimaryButton>
          </div>
          <div className="flex justify-center">
            <a href={calculatedLink} className="text-brand-primary" target={'_self'}>
              {t('dashboard.settings.user_plan.upgrade_change_payment_method_title')}
            </a>
          </div>
          <FieldErrorAndDescription error={error} errorClassName={'text-center mt-2'} />
        </form>
      </Modal>
    </>;
};
export default UpgradePlanConfirmationModal;